import { classifyCombinablePromotions } from '@/components/blocks/FreeGiftV2/utils';
import { CombinablePromotion } from '@/data/types/Promotions';
import { MULTI_FREE_GIFT_PROMO } from 'presentation/events/constants/eventConstants';
import { PromotionDetails } from 'presentation/events/hooks/useGetPromotionDetails';
import {
	PredictFreeGiftCartItem,
	PredictFreeGiftResult,
} from 'presentation/events/utils/freeProductUtils';
import { getItemPartNumber, getProductQuantity } from 'presentation/events/utils/productUtils';
import { IS_PARTNUMBER_FROM_BUNDLE_KEY } from 'shared/custom/constants/commonConstants';
import { FreeGiftInput, PredictFreeGiftParams } from './types';

export const enrichProductWithFreeGift = ({
	product,
	quantity,
	physicalStoreId,
	cartItems,
	cartItemsPromotionDetails,
}: FreeGiftInput): FreeGiftInput['product'] => {
	if (product.isGift || !physicalStoreId || cartItemsPromotionDetails.size === 0) {
		return product;
	}

	const prediction = getBestFreeGiftMatch({
		productPartNumber: getItemPartNumber(product),
		productCatentryId: product.id,
		addQuantity: getProductQuantity({ quantity }),
		cartItems,
		cartItemsPromotionDetails,
		physicalStoreId,
	});

	if (prediction.isGift) {
		return {
			...product,
			isGift: true as const,
			freeQty: prediction.freeQty,
			promoThdCode: prediction.promoThdCode ?? product.promoThdCode,
		};
	}

	return product;
};

const calcFreeQtyForPromotion = ({
	promotionData,
	productCatentryId,
	cartItems,
	addQuantity,
	physicalStoreId,
}: {
	promotionData: NonNullable<PromotionDetails['promotionData']>;
	productCatentryId: string;
	cartItems: PredictFreeGiftCartItem[];
	addQuantity: number;
	physicalStoreId: string;
}): number => {
	if (promotionData.field1 !== MULTI_FREE_GIFT_PROMO) return 0;
	if (promotionData.active !== true) return 0;
	if (!promotionData.physicalStores?.includes(physicalStoreId)) return 0;

	const giftCatentries = promotionData.giftCatentries;
	if (!giftCatentries?.includes(productCatentryId)) return 0;

	const baseQty = promotionData.baseQty;
	const giftQty = promotionData.giftQty;
	if (!baseQty || !giftQty) return 0;

	const relatedCatentries = promotionData.relatedCatentries;
	const parentCatentriesSet = new Set(relatedCatentries ?? []);
	const totalParentQtyInCart = cartItems
		.filter((item) => parentCatentriesSet.has(item.productId))
		.reduce((sum, item) => sum + parseFloat(item.quantity), 0);

	const promoApplications = Math.floor(totalParentQtyInCart / baseQty);
	if (promoApplications <= 0) return 0;

	const giftCatentriesSet = new Set(giftCatentries);
	const totalGiftQtyInCart = cartItems
		.filter((item) => giftCatentriesSet.has(item.productId))
		.reduce((sum, item) => sum + parseFloat(item.quantity), 0);

	const newTotalGifts = totalGiftQtyInCart + addQuantity;

	const maxBlocks = promoApplications;
	const totalGiftBlocks = Math.min(Math.floor(newTotalGifts / giftQty), maxBlocks);
	const totalFreeAfterAdd = totalGiftBlocks * giftQty;

	const existingBlocks = Math.min(Math.floor(totalGiftQtyInCart / giftQty), maxBlocks);
	const existingFree = existingBlocks * giftQty;

	const freeInThisAdd = totalFreeAfterAdd - existingFree;
	if (freeInThisAdd <= 0) return 0;

	return Math.min(addQuantity, freeInThisAdd);
};

const getBestFreeGiftMatch = (params: PredictFreeGiftParams): PredictFreeGiftResult => {
	const { productCatentryId, addQuantity, cartItems, cartItemsPromotionDetails, physicalStoreId } =
		params;

	const processedPromos = new Set<string>();

	const best = Array.from(cartItemsPromotionDetails.values()).reduce<{
		freeQty: number;
		promoDetails?: PromotionDetails;
	}>(
		(acc, promoDetails) => {
			if (!promoDetails.promotionData) return acc;

			const promoName = promoDetails.promotionData.PromotionName;
			if (promoName && processedPromos.has(promoName)) return acc;
			if (promoName) processedPromos.add(promoName);

			const freeQty = calcFreeQtyForPromotion({
				promotionData: promoDetails.promotionData,
				productCatentryId,
				cartItems,
				addQuantity,
				physicalStoreId,
			});

			return freeQty > acc.freeQty ? { freeQty, promoDetails } : acc;
		},
		{ freeQty: 0 }
	);

	if (best.freeQty <= 0) {
		return { isGift: false, freeQty: undefined };
	}

	const promoThdCode = classifyCombinablePromotions(
		best.promoDetails?.combinablePromotions as CombinablePromotion[] | undefined
	);

	return {
		isGift: true,
		freeQty: String(best.freeQty),
		promoThdCode,
	};
};

export const getBundlesMap = (): Map<string, string> => {
	const raw = sessionStorage.getItem(IS_PARTNUMBER_FROM_BUNDLE_KEY);

	if (!raw) return new Map();

	try {
		const obj = JSON.parse(raw);
		if (obj === null || typeof obj !== 'object') {
			return new Map();
		}
		return new Map(Object.entries(obj));
	} catch (error) {
		return new Map();
	}
};

export const saveBundlesMap = (map: Map<string, string>) => {
	const obj = Object.fromEntries(map);
	sessionStorage.setItem(IS_PARTNUMBER_FROM_BUNDLE_KEY, JSON.stringify(obj));
};
